<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import FormSummaryCard from './FormSummaryCard.vue';
import BaseLoading from '../base/BaseLoading.vue';

import { getTranslation } from '@/composables/data-display-utils';

import type { Locales, SummarySectionDto, SummaryFormDto, SummaryExportingOptions } from '@/services/types/api.types';
import { useSummariesStore } from '@/stores/summaries.store';

// CONST
const { t } = useI18n();

// STORE
const summariesStore = useSummariesStore();

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  section: SummarySectionDto;
}>();

// EMITS
const emit = defineEmits<{
  (e: 'onExportingSummary', options: SummaryExportingOptions): void;
}>();

// REFS
const forms = ref<SummaryFormDto[] | null>(null);

// FUNCTIONS
const loadSectionForms = async () => {
  forms.value = await summariesStore.getForms(props.section.id);
};

const onExportingSummary = (options: SummaryExportingOptions | Event) => {
  const event = options as Event;
  emit('onExportingSummary', event.target ? { sectionId: props.section.id } : (options as SummaryExportingOptions));
};
</script>
<template>
  <div class="section-summary-card">
    <q-list class="rounded-borders">
      <q-expansion-item expand-separator class="section-item-header" @show="loadSectionForms">
        <template v-slot:header>
          <div class="section-item-content">
            <div class="section-item-content__icon">
              <q-icon name="o_folder" />
            </div>
            <div class="section-item-content__label">
              <span
                >{{ getTranslation(props.section, 'title', props.languageEnabled) }} ({{
                  props.section.number_of_forms
                }})</span
              >
            </div>
          </div>
          <div class="section-item-menu">
            <q-btn icon="more_horiz" flat dense @click.stop>
              <q-menu class="q-actions-menu">
                <q-list>
                  <q-item clickable @click="onExportingSummary" v-close-popup>
                    <q-item-section>{{ t('global.export') }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </template>
        <q-card>
          <BaseLoading
            class="section-summary-card__loading"
            :loading-text="$t('global.loading')"
            :show-spinner="summariesStore.isFormsLoading(section.id)"
          />
          <FormSummaryCard
            v-for="form in forms"
            :key="form.id"
            :form="form"
            :language-enabled="props.languageEnabled"
            @on-exporting-summary="onExportingSummary"
          />
          <div v-if="forms && forms.length === 0" class="section-summary-card__no-forms">
            {{ t('section.no-forms') }}
          </div>
        </q-card>
      </q-expansion-item>
    </q-list>
  </div>
</template>
<style lang="scss">
.section-summary-card {
  .section-item-header {
    color: $color-neutral-white;
    border-radius: 6px;

    .section-item-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      gap: $space-md;
      padding: $space-sm $space-md;

      .q-icon {
        font-size: $medium-title;
      }
    }

    .section-item-menu {
      align-content: center;
    }
  }

  .q-item__section {
    .q-icon {
      color: $color-neutral-white;
    }
  }

  &__no-forms {
    font-size: $small-text;
    padding: $space-md;
    background-color: $color-neutral-white;
    border-radius: 6px !important;
    overflow: hidden;
    color: $color-neutral-black-text !important;
    text-align: center;
  }

  &__loading {
    margin: $space-2xl 0;

    .base-loading__text {
      color: $color-neutral-black-text;
    }
  }
}

.section-summary-card > .q-list > .q-expansion-item > .q-expansion-item__container > .q-item,
.q-expansion-item--expanded > .q-expansion-item__container > .q-item > .q-card {
  border-radius: 6px !important;
  overflow: hidden;
  background-color: $color-primary;
}

.section-summary-card > .q-list > .q-expansion-item > .q-expansion-item__container > .q-item {
  transition: margin-bottom 260ms ease;
}

.section-summary-card > .q-list > .q-expansion-item--expanded > .q-expansion-item__container > .q-item {
  margin-bottom: $space-md;
}

.section-summary-card > .q-list > .q-expansion-item--expanded > .q-expansion-item__container > .q-expansion-item {
  border-radius: 6px;
  overflow: hidden;
}

.section-summary-card > .q-list > .q-expansion-item--collapsed > .q-expansion-item__container > .q-item {
  margin-bottom: 0;
}

.section-summary-card > .q-list .q-expansion-item__container > .q-item > .q-btn > .q-focus-helper {
  display: none;
}

.section-summary-card
  > .q-list
  > .q-expansion-item--expanded
  > .q-expansion-item__container
  > .q-expansion-item__content
  > .q-card {
  background-color: transparent !important;
}
</style>
